/**
 * La identidad visual de cada producto en las portadas de novedades (goal
 * novedades-del-portal 2, decisión 5 de Víctor: la imagen se PINTA, no se sube).
 *
 * Módulo puro y sin React a propósito: la tabla y su caso por defecto son la
 * parte que hay que poder probar sin montar nada.
 *
 * 🚨 **Aquí ya no se declara ningún producto: todo sale de
 * `catalogo-de-productos.json`**, que es la fuente ÚNICA del ecosistema (goal
 * `portal-correcciones 12`). Hasta el 2026-09-14 la lista vivía dos veces —aquí
 * y en la allowlist del skill `promover-staging-production`— y desincronizarlas
 * era un fallo mudo: el producto publicaba bien y salía en la portada gris.
 * Pasó con «punto de venta web» y «Kariqui (agentes de IA)». Dar de alta un
 * producto es **editar ese JSON y nada más**.
 */
import catalogo from './catalogo-de-productos.json'

/** Clave interna del producto; decide qué icono pinta la portada. */
export type ClaveDeProducto = keyof typeof catalogo

/** Lo que el catálogo dice de un producto. */
export interface ProductoDelCatalogo {
  /** El `product` EXACTO que el skill escribe en cada nota de versión. */
  rotulo: string
  /** Tonalidad OKLCH del degradado. */
  hue: number
  /** Nombre del icono de lucide; `PortadaProducto` lo resuelve a componente. */
  icono: string
  /** Repos que publican notas con este rótulo. */
  repos: string[]
  /**
   * Repo que PORTA la nota cuando el producto vive en varios repos: los demás
   * no publican una segunda del mismo corte, o el cliente recibe dos avisos de
   * WhatsApp con el mismo texto. `null` = cada repo publica lo suyo.
   */
  portador: string | null
  /** Lo que hay que saber al escribir su nota. Vacío si no hay nada. */
  nota: string
}

/**
 * El catálogo entero, tipado. **Lo consume también el paso 2e del skill
 * `promover-staging-production`**, que lo lee de este repo con `gh api` en vez
 * de tener su propia copia — mover o renombrar el JSON rompe ese paso.
 */
export const CATALOGO_DE_PRODUCTOS: Record<
  ClaveDeProducto,
  ProductoDelCatalogo
> = catalogo

export interface PortadaDeProducto {
  /** `null` = producto desconocido: portada grafito con la inicial. */
  clave: ClaveDeProducto | null
  /** Tonalidad OKLCH del degradado, o `null` para el gris del caso por defecto. */
  hue: number | null
  /** Primera letra del rótulo, en mayúscula. Solo se pinta si `clave` es null. */
  inicial: string
}

/** Todas las claves del catálogo, en su orden de declaración. */
export const CLAVES_DE_PRODUCTO = Object.keys(
  CATALOGO_DE_PRODUCTOS,
) as ClaveDeProducto[]

/**
 * Rótulo normalizado → clave, **derivado del catálogo**. Antes era una tabla a
 * mano y era la mitad que se olvidaba.
 *
 * ⚠️ Dos rótulos que normalicen igual se pisarían en silencio (el último gana);
 * lo impide un candado de `productoVisual.test.ts`, que es donde vive todo lo
 * que el tipo no puede exigirle a un JSON.
 */
const CLAVE_POR_ROTULO: Record<string, ClaveDeProducto> = Object.fromEntries(
  CLAVES_DE_PRODUCTO.map((clave) => [
    claveVisual(CATALOGO_DE_PRODUCTOS[clave].rotulo),
    clave,
  ]),
)

/**
 * Clave de comparación de la portada: minúsculas, espacios colapsados y **sin
 * tildes**.
 *
 * ⚠️ Diverge a propósito de `claveDeRotulo` de `features/releases/
 * productGroups.ts` (y de su gemela en el servidor), que NO toca las tildes. La
 * asimetría es deliberada porque el daño de equivocarse es distinto: allí
 * plegar de más partiría —o fundiría— productos en el filtro del histórico,
 * mientras que aquí lo peor que pasa por acertar de más es pintar el icono
 * bueno de un rótulo escrito sin acento. Doblar las tildes solo puede REDUCIR
 * los que caen en la portada por defecto.
 */
export function claveVisual(product: string): string {
  return product
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/** Primera letra o dígito del rótulo, en mayúscula. `?` si no hay ninguno. */
function inicialDe(product: string): string {
  const match = product.normalize('NFC').match(/[\p{L}\p{N}]/u)
  return match ? match[0].toLocaleUpperCase('es') : '?'
}

/**
 * Qué portada le toca a un producto.
 *
 * 🚨 El caso por defecto es el motivo por el que esta función existe: el
 * `product` es **texto libre** que el pipeline de release reescribe en cada
 * corte, así que un producto nuevo —o uno renombrado— tiene que caer en una
 * portada digna, nunca en un hueco roto. La inicial además distingue dos
 * desconocidos entre sí.
 */
export function portadaDeProducto(product: string): PortadaDeProducto {
  const clave = CLAVE_POR_ROTULO[claveVisual(product)] ?? null
  return {
    clave,
    hue: clave ? CATALOGO_DE_PRODUCTOS[clave].hue : null,
    inicial: inicialDe(product),
  }
}

/**
 * El degradado de la portada. Una sola fórmula para las trece: cambia la
 * tonalidad y nada más. El caso por defecto sale del mismo molde en grafito
 * (croma cero), para que no parezca de otro juego.
 */
export function fondoDePortada(hue: number | null): string {
  const brillo =
    'radial-gradient(120% 90% at 22% 12%, rgba(255,255,255,.34), transparent 62%)'
  if (hue === null) {
    return `${brillo}, linear-gradient(142deg, oklch(0.60 0.012 265), oklch(0.42 0.014 265))`
  }
  return `${brillo}, linear-gradient(142deg, oklch(0.66 0.145 ${hue}), oklch(0.47 0.155 ${hue - 16}))`
}
